"use client"

import { useState, useEffect } from "react"
import { ArrowUp, ArrowDown, DollarSign, Users, ShoppingBag, AlertCircle, TrendingUp, TrendingDown } from "lucide-react"
import { Link } from "react-router-dom"
import Chart from "../components/Chart"
import { Line, Bar, Doughnut } from 'react-chartjs-2'
import ActionModal from "../components/ActionModal"
import { api } from "../utils/api"
import API from "../config/apiPaths"
import AddPartyModal from '../components/AddPartyModal'

export default function Dashboard() {
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [timeRange, setTimeRange] = useState("daily")
  const [showAddModal, setShowAddModal] = useState(false)
  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split("T")[0],
    partyName: "",
    type: "credit",
    amount: "",
    description: "",
    paymentMode: "Cash",
  })
  const [dateRange, setDateRange] = useState('week')
  const [showModal, setShowModal] = useState(false)
  const [modalAction, setModalAction] = useState(null)
  const [showAddPartyModal, setShowAddPartyModal] = useState(false)

  useEffect(() => {
    async function fetchDashboard() {
      setLoading(true)
      setError(null)
      try {
        const res = await api.get(API.DASHBOARD.SUMMARY)
        if (res.data.success) {
          setDashboard(res.data.data)
        } else {
          setError(res.data.message || 'Failed to load dashboard')
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchDashboard()
  }, [])

  // Helper: fallback to 0 if dashboard not loaded
  const safe = (v) => (dashboard ? v : 0)

  // Format chart data from API
  const formatChartData = (data, labelKey) => {
    const safeData = Array.isArray(data) ? data : [];
    return {
      labels: safeData.map(item => item[labelKey]),
      datasets: [
        {
          label: 'Income',
          data: safeData.map(item => item.income),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1,
          fill: true,
        },
        {
          label: 'Expenses',
          data: safeData.map(item => item.expenses),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.1,
          fill: true,
        },
      ],
    }
  }

  // Format payment modes data (this is still dummy as not in API)
  const paymentModesData = {
    labels: ['Cash', 'UPI', 'Bank Transfer', 'Card', 'Other'],
    datasets: [
      {
        data: [35, 30, 20, 10, 5],
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)',
        ],
      },
    ],
  }

  // Format weekly volume data (this is still dummy as not in API)
  const weeklyVolumeData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Transactions',
        data: [25, 35, 28, 32, 42, 38, 15],
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      },
    ],
  }

  // Select data based on time range
  const getChartData = () => {
    if (!dashboard) return { labels: [], datasets: [] }
    
    if (timeRange === "daily") {
      return formatChartData(dashboard.dailyChartData, 'date')
    } else if (timeRange === "weekly") {
      return formatChartData(dashboard.weeklyChartData, 'week')
    } else {
      return formatChartData(dashboard.monthlyChartData, 'month')
    }
  }

  // Calculate totals from API
  const totalIncome = safe(dashboard?.totalIncome)
  const totalExpense = safe(dashboard?.totalExpenses)
  const totalBalance = safe(dashboard?.netProfit)
  const totalReceivable = safe(dashboard?.pendingReceivables)
  const totalPayable = safe(dashboard?.pendingPayables)
  const totalCustomers = safe(dashboard?.totalCustomers)
  const totalSuppliers = safe(dashboard?.totalSuppliers)
  const totalProducts = safe(dashboard?.totalProducts)
  const lowStockProducts = safe(dashboard?.lowStockProducts)

  // Loading spinner
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <svg className="animate-spin h-10 w-10 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-100 text-red-700 px-6 py-4 rounded shadow">{error}</div>
      </div>
    )
  }

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNewTransaction({
      ...newTransaction,
      [name]: name === "amount" ? (value === "" ? "" : Number(value)) : value,
    })
  }

  // Handle add transaction
  const handleAddTransaction = () => {
    console.log("Adding transaction:", newTransaction)
    // Here you would typically add the transaction to your state or send to an API
    setShowAddModal(false)
    setNewTransaction({
      date: new Date().toISOString().split("T")[0],
      partyName: "",
      type: "credit",
      amount: "",
      description: "",
      paymentMode: "Cash",
    })
    alert("Transaction added successfully!")
  }

  const handleActionClick = (action) => {
    setModalAction(action)
    setShowModal(true)
  }

  return (
    <div className="container mx-auto px-4">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <div className="mt-4 md:mt-0 flex space-x-3">
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            + Add Transaction
          </button>
          <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900">
            Generate Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Balance</h3>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">₹{(totalBalance ?? 0).toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-sm text-green-600">Net Profit</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Receivables</h3>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">₹{(totalReceivable ?? 0).toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <ArrowDown className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-sm text-blue-600">Pending Receivables</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Payables</h3>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">₹{(totalPayable ?? 0).toLocaleString()}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <ArrowUp className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-sm text-red-600">Pending Payables</span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Customers</h3>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{totalCustomers}</p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-full">
              <Users className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
          <div className="mt-2">
            <span className="text-sm text-indigo-600">Total Customers</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <button onClick={() => handleActionClick('add-sale')} className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow text-center hover:bg-gray-50 dark:hover:bg-gray-700">
          <svg className="h-6 w-6 text-indigo-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">Add Sale</span>
        </button>
        <button onClick={() => setShowAddPartyModal(true)} className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow text-center hover:bg-gray-50 dark:hover:bg-gray-700">
          <svg className="h-6 w-6 text-indigo-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">Add Party</span>
        </button>
        <button onClick={() => handleActionClick('add-purchase')} className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow text-center hover:bg-gray-50 dark:hover:bg-gray-700">
          <svg className="h-6 w-6 text-indigo-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">Add Purchase</span>
        </button>
        <button onClick={() => handleActionClick('generate-report')} className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow text-center hover:bg-gray-50 dark:hover:bg-gray-700">
          <svg className="h-6 w-6 text-indigo-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">Generate Report</span>
        </button>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Income vs Expenses</h3>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="rounded-md text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <Line
            data={getChartData()}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: (value) => '₹' + value.toLocaleString('en-IN'),
                  },
                },
              },
            }}
          />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Payment Mode Distribution</h3>
          <div className="aspect-w-2 aspect-h-1">
            <Doughnut
              data={paymentModesData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'right',
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Weekly Transaction Volume</h3>
          <Bar
            data={weeklyVolumeData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Inventory Summary</h3>
            <a href="/inventory" className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
              View All
            </a>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Products</h4>
              <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">{totalProducts}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Low Stock</h4>
              <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">{lowStockProducts}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Suppliers</h4>
              <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">{totalSuppliers}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Income</h4>
              <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">₹{(totalIncome ?? 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Transaction Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Add New Transaction</h3>
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Date
                          </label>
                          <input
                            type="date"
                            name="date"
                            id="date"
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            value={newTransaction.date}
                            onChange={handleInputChange}
                          />
                        </div>

                        <div>
                          <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Type
                          </label>
                          <select
                            name="type"
                            id="type"
                            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            value={newTransaction.type}
                            onChange={handleInputChange}
                          >
                            <option value="credit">Credit (Receive)</option>
                            <option value="debit">Debit (Pay)</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="partyName"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Party Name
                        </label>
                        <input
                          type="text"
                          name="partyName"
                          id="partyName"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          value={newTransaction.partyName}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div>
                        <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Amount
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 dark:text-gray-400 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="number"
                            name="amount"
                            id="amount"
                            className="block w-full pl-7 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            value={newTransaction.amount}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="description"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Description
                        </label>
                        <input
                          type="text"
                          name="description"
                          id="description"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          value={newTransaction.description}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="paymentMode"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          Payment Mode
                        </label>
                        <select
                          name="paymentMode"
                          id="paymentMode"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          value={newTransaction.paymentMode}
                          onChange={handleInputChange}
                        >
                          <option value="Cash">Cash</option>
                          <option value="UPI">UPI</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Cheque">Cheque</option>
                          <option value="Credit Card">Credit Card</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleAddTransaction}
                >
                  Add Transaction
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ActionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        action={modalAction}
      />

      <AddPartyModal open={showAddPartyModal} onClose={() => setShowAddPartyModal(false)} onAddParty={() => {}} />
    </div>
  )
}